import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Server, Network, TrendingUp } from 'lucide-react';
import { translations } from '../utils/translations';

export const Hero = ({ language = 'en' }) => {
  const t = translations[language]?.hero || translations.en.hero;

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const floatingIcons = [
    { Icon: Code, delay: 0, x: '10%', y: '20%' },
    { Icon: Server, delay: 0.2, x: '80%', y: '30%' },
    { Icon: Network, delay: 0.4, x: '15%', y: '70%' },
    { Icon: TrendingUp, delay: 0.6, x: '85%', y: '75%' },
  ];

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center overflow-hidden bg-white dark:bg-gray-900 transition-colors duration-300"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {floatingIcons.map(({ Icon, delay, x, y }, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.1, scale: 1 }}
            transition={{ 
              delay, 
              duration: 1, 
              repeat: Infinity, 
              repeatType: 'reverse', 
              repeatDelay: 2 
            }}
            className="absolute"
            style={{ left: x, top: y }}
          >
            <Icon size={80} className="text-blue-600 dark:text-blue-400" />
          </motion.div>
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-block mb-4 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full"
            >
              <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                {t.badge}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 dark:text-white leading-tight mb-6"
            >
              {t.title.beforeHighlight}{' '}
              <span className="text-blue-600 dark:text-blue-400">
                {t.title.highlight}
              </span>
              {t.title.afterHighlight && ` ${t.title.afterHighlight}`}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8"
            >
              {t.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={scrollToContact}
                className="group px-8 py-4 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-all duration-300 hover:shadow-lg font-semibold flex items-center justify-center gap-2"
              >
                {t.buttons.primary}
                <ArrowRight 
                  size={20} 
                  className="group-hover:translate-x-1 transition-transform duration-300" 
                />
              </button>
              <button
                onClick={scrollToServices}
                className="px-8 py-4 border-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white rounded-lg transition-all duration-300 font-semibold"
              >
                {t.buttons.secondary}
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="grid grid-cols-3 gap-6 mt-12"
            >
              <div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {t.stats.projects}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {t.stats.projectsLabel}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {t.stats.experience}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {t.stats.experienceLabel}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {t.stats.satisfaction}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {t.stats.satisfactionLabel}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Tech Illustration */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Main Card */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700"
              >
                <div className="space-y-4">
                  <div className="h-4 bg-gradient-to-r from-blue-600 to-blue-400 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-2/3"></div>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="h-20 bg-blue-100 dark:bg-blue-900/30 rounded-lg"></div>
                    <div className="h-20 bg-blue-100 dark:bg-blue-900/30 rounded-lg"></div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Cards */}
              <motion.div
                animate={{ y: [0, 15, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-4 -right-4 bg-blue-500 text-white p-4 rounded-xl shadow-lg"
              >
                <Code size={32} />
              </motion.div>

              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-4 -left-4 bg-blue-600 text-white p-4 rounded-xl shadow-lg"
              >
                <Server size={32} />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-blue-600 dark:border-blue-400 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};